import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import TranslationStatusFilter from "../../common/Filters/TranslationStatusFilter";
import styles from "./TranslationStatus.module.scss";
import TranslationSummaryChart from "../../../components/Chart/TranslationSummaryChart";
import LanguageCoverageChart from "../../../components/Chart/LanguageCoverageChart";
import { PdApiActions, pdApiActions } from "../../../actions/pdApiActions";
import Table, { createColumn, createPercentageColumn } from "../../../components/Table/Table";

type StateProps = {
  pdApi: State.PdApi;
  filterOptions: State.TranslationStatusFilterOptions;
  isLoadingStatistics: boolean;
};

type DispatchProps = {
  pdApiActions: typeof pdApiActions;
};

export type TranslationStatusProps = StateProps & DispatchProps;

type TranslationStatusState = {
  isModalOpen: boolean;
};

export class TranslationStatus extends React.Component<
  TranslationStatusProps,
  TranslationStatusState
> {
  constructor(props: TranslationStatusProps) {
    super(props);
    this.UpdateChartData = this.UpdateChartData.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
  }
  state = {
    isModalOpen: false,
    filterOptions: this.props.filterOptions
  };

  componentDidMount(): void {
    this.UpdateChartData(this.state.filterOptions);
  }

  handleFilterChange(options: State.TranslationStatusFilterOptions): void {
    this.UpdateChartData(options);
  }

  UpdateChartData(options: State.TranslationStatusFilterOptions): void {
    this.props.pdApiActions.setItemTypeLanguageCoverage([]);
    this.props.pdApiActions.fetchChartDataSingle(
      "translationSummary",
      options.selectedDomainGroupEnumValue,
      options.selectedItemTypeId,
      options.selectedDomainEnumValue
    );
    this.props.pdApiActions.fetchChartDataSingle(
      "languageCoverage",
      options.selectedDomainGroupEnumValue,
      options.selectedItemTypeId,
      options.selectedDomainEnumValue
    );
    this.props.pdApiActions.fetchChartDataSingle(
      "itemTypeLanguageCoverage",
      options.selectedDomainGroupEnumValue,
      options.selectedItemTypeId,
      options.selectedDomainEnumValue
    );
  }

  render(): JSX.Element {
    const MAX_SERIES_IN_CHART = 30;
    const { pdApi, isLoadingStatistics } = this.props;

    const translationSummary = pdApi.chartData.translationSummary.map(
      item =>
        ({
          label: item.label
            .replace("TranslatedCount", item.value + " TRANSLATED")
            .replace("InTranslationCount", item.value + " IN TRANSLATION"),
          value: item.value
        } as Api.SeriesDataset)
    );
    const languageCoverage = pdApi.chartData.languageCoverage.sort((a, b) =>
      a.value < b.value ? 1 : -1
    );
    const moreThanMax = languageCoverage.length > MAX_SERIES_IN_CHART;
    const languageCoverageLargest = moreThanMax
      ? languageCoverage.slice(0, MAX_SERIES_IN_CHART / 2)
      : languageCoverage;
    const languageCoverageSmallest = moreThanMax
      ? languageCoverage.slice(-MAX_SERIES_IN_CHART / 2)
      : [];

    const filters = (
      <>
        <TranslationStatusFilter filterTarget="domainGroup" onChange={this.handleFilterChange} />
        <TranslationStatusFilter filterTarget="itemType" onChange={this.handleFilterChange} />
        <TranslationStatusFilter filterTarget="domain" onChange={this.handleFilterChange} />
      </>
    );

    const columns: State.Column[] =
      pdApi.chartData.itemTypeLanguageCoverage.length == 0
        ? []
        : [
            ...[createColumn("itemType", item => item.itemType, "item.itemType", true)],
            ...pdApi.chartData.itemTypeLanguageCoverage[0].translationCoverages.map(itm =>
              createPercentageColumn(
                itm.label,
                item => item[itm.label] + "%",
                itm.label,
                true,
                120,
                undefined,
                false,
                undefined,
                "center"
              )
            )
          ];
          

    const rows = pdApi.chartData.itemTypeLanguageCoverage
      .filter(itm => itm.translationCoverages.length > 0)
      .map(itm => {
        const object = itm.translationCoverages.reduce(
          (obj, item) => ({ ...obj, [item.label]: item.value }),
          {}
        );
        return { ...object, itemType: itm.itemType };
      });

    return (
      <>
        <div className={styles.filtersContainer}>
          {!!filters && <div className={styles.filters}>{filters}</div>}
        </div>
        <div className={styles.summaryChartsContainer}>
          <div className={styles.translationSummary}>
            <span>TRANSLATION SUMMARY</span>
            <TranslationSummaryChart
              datasets={translationSummary}
              option={"translationSummary"}
              height="110px"
              width="460px"
              legendLabelPadding={7}
            />
          </div>
          <div className={styles.languageCoverageContainer}>
            LANGUAGE COVERAGE
            <div className={styles.languageCoverage}>
              {!moreThanMax && (
                <>
                  <LanguageCoverageChart
                    datasets={languageCoverageLargest}
                    option={"languageCoverage"}
                    height="140px"
                    width={moreThanMax ? "600px" : "1300px"}
                    displayYAxis={true}
                  />
                </>
              )}
              {moreThanMax && (
                <>
                  <LanguageCoverageChart
                    datasets={languageCoverageLargest}
                    option={"languageCoverage"}
                    height="140px"
                    width={moreThanMax ? "600px" : "1300px"}
                    displayYAxis={true}
                  />
                  <span>...</span>
                  <LanguageCoverageChart
                    datasets={languageCoverageSmallest}
                    option={"languageCoverage"}
                    height="140px"
                    width="600px"
                  />
                </>
              )}
            </div>
          </div>
        </div>
        <div className="translationPercentTable">
          <Table
            rows={rows}
            columns={columns}
            loading={isLoadingStatistics}
            loadingText="Fetching statistics..."
            isHidingTextActive={true}
            handleRowSelection={() => {
              /* No selection handling */
            }}
            isRowSelected={() => false}
            pageSize={100}
            disablePagination={true}
          />
        </div>
      </>
    );
  }
}

const mapStateToProps = (state: State.Root) => ({
  filterOptions: state.translationStatusFilterOptions
});

const mapDispatchToProps = (dispatch: Dispatch<PdApiActions>) => ({
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(TranslationStatus);
